import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Icons from 'lucide-react';
import { aboutData } from '../mockPages';
import ContactForm from '../components/ContactForm';

const AboutPage = () => {
  const { t } = useTranslation();

  const getIconComponent = (iconName) => {
    const iconMap = {
      'brain': Icons.Brain,
      'heart': Icons.Heart,
      'globe': Icons.Globe,
      'shield': Icons.Shield,
      'users': Icons.Users,
      'lightbulb': Icons.Lightbulb,
      'target': Icons.Target,
      'award': Icons.Award,
      'check': Icons.CheckCircle,
      'leaf': Icons.Leaf,
    };
    return iconMap[iconName] || Icons.Circle;
  };

  return (
    <div style={{ background: 'var(--bg-page)', paddingTop: '80px' }}>
      {/* Hero Section */}
      <section
        style={{
          background: '#0A1628',
          padding: '5rem 1.2rem',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url(https://images.unsplash.com/photo-1505751172876-fa1923c5c528?q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.15,
            zIndex: 0,
          }}
        />
        
        <div
          style={{
            position: 'absolute',
            top: '10%',
            left: '5%',
            width: '300px',
            height: '300px',
            background: 'radial-gradient(circle, rgba(34, 197, 94, 0.2) 0%, transparent 70%)',
            borderRadius: '50%',
            filter: 'blur(60px)',
            zIndex: 0,
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: '10%',
            right: '10%',
            width: '250px',
            height: '250px',
            background: 'radial-gradient(circle, rgba(245, 158, 11, 0.2) 0%, transparent 70%)',
            borderRadius: '50%',
            filter: 'blur(60px)',
            zIndex: 0,
          }}
        />

        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <h1 className="heading-hero" style={{ marginBottom: '1rem' }}>
            {t('about.title')}
          </h1>
          <p className="heading-2" style={{ color: '#22c55e', marginBottom: '2rem', textShadow: '0 0 20px rgba(34, 197, 94, 0.5)' }}>
            {t('about.subtitle')}
          </p>
          <p
            className="body-large"
            style={{
              maxWidth: '900px',
              margin: '0 auto 3rem',
              color: 'var(--text-secondary)',
              lineHeight: '1.8',
            }}
          >
            {t('about.storyContent')}
          </p>

          {/* Stats */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '2rem',
              maxWidth: '800px',
              margin: '0 auto',
            }}
          >
            {[
              { label: t('about.founded'), value: '2025' },
              { label: t('about.countries'), value: '2+' },
              { label: t('about.hospitals'), value: '10+' },
              { label: t('about.teamMembers'), value: '75+' },
            ].map((stat, index) => (
              <div
                key={index}
                style={{
                  background: 'rgba(34, 197, 94, 0.1)',
                  backdropFilter: 'blur(8px)',
                  borderRadius: '1rem',
                  padding: '1.5rem',
                  border: '2px solid rgba(34, 197, 94, 0.4)',
                }}
              >
                <div
                  style={{
                    fontSize: '2rem',
                    fontWeight: '700',
                    color: '#22c55e',
                    marginBottom: '0.5rem',
                    textShadow: '0 0 15px rgba(34, 197, 94, 0.4)',
                  }}
                >
                  {stat.value}
                </div>
                <div className="body-small" style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section style={{ padding: '5rem 1.2rem', background: 'var(--bg-section)' }}>
        <div className="container">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '2rem',
            }}
          >
            {/* Mission */}
            <div
              style={{
                background: 'rgba(34, 197, 94, 0.1)',
                borderRadius: '1rem',
                padding: '2rem',
                border: '1px solid rgba(34, 197, 94, 0.3)',
              }}
            >
              <div
                className="icon-container-ai"
                style={{
                  width: '3.5rem',
                  height: '3.5rem',
                  borderRadius: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '1.5rem',
                }}
              >
                <Icons.Target size={28} color="white" />
              </div>
              <h2 className="heading-2" style={{ marginBottom: '1rem' }}>
                {t('about.missionTitle')}
              </h2>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                {t('about.missionContent')}
              </p>
            </div>

            {/* Vision */}
            <div
              style={{
                background: 'rgba(245, 158, 11, 0.1)',
                borderRadius: '1rem',
                padding: '2rem',
                border: '1px solid rgba(245, 158, 11, 0.3)',
              }}
            >
              <div
                className="icon-container-ayurveda"
                style={{
                  width: '3.5rem',
                  height: '3.5rem',
                  borderRadius: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '1.5rem',
                }}
              >
                <Icons.Lightbulb size={28} color="white" />
              </div>
              <h2 className="heading-2" style={{ marginBottom: '1rem' }}>
                {t('about.visionTitle')}
              </h2>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                {t('about.visionContent')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section style={{ padding: '5rem 1.2rem', background: 'var(--bg-page)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              {t('about.coreValuesTitle')}
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              {t('about.coreValuesSubtitle')}
            </p>
          </div>

          <div
            className="core-values-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '1.5rem',
              maxWidth: '1100px',
              margin: '0 auto',
            }}
          >
            {[
              { key: 'innovation', icon: 'lightbulb' },
              { key: 'patientCare', icon: 'heart' },
              { key: 'excellence', icon: 'award' },
              { key: 'global', icon: 'globe' },
              { key: 'learning', icon: 'users' },
              { key: 'sustainable', icon: 'leaf' },
            ].map((value, index) => {
              const IconComponent = getIconComponent(value.icon);
              const colors = ['#22c55e', '#f59e0b', '#ec4899', '#06b6d4', '#8b5cf6', '#f97316'];
              const color = colors[index % colors.length];

              return (
                <div
                  key={value.key}
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '1rem',
                    padding: '1.5rem',
                    border: `1px solid ${color}33`,
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                  }}
                  className="hover-lift"
                >
                  <div
                    style={{
                      width: '3rem',
                      height: '3rem',
                      borderRadius: '50%',
                      background: `${color}22`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 1rem',
                      border: `2px solid ${color}`,
                    }}
                  >
                    <IconComponent size={24} color={color} />
                  </div>
                  <h3 className="heading-3" style={{ marginBottom: '0.5rem', color }}>
                    {t(`about.values.${value.key}`)}
                  </h3>
                  <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    {t(`about.values.${value.key}Desc`)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section style={{ padding: '5rem 1.2rem', background: 'var(--bg-page)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
              {t('about.leadershipTitle')}
            </h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              {t('about.leadershipSubtitle')}
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '2rem',
              maxWidth: '1000px',
              margin: '0 auto',
            }}
          >
            {/* Dr. Kumar */}
            <div
              style={{
                background: 'rgba(34, 197, 94, 0.1)',
                borderRadius: '1rem',
                padding: '2rem',
                textAlign: 'center',
                border: '2px solid rgba(34, 197, 94, 0.3)',
                boxShadow: '0 4px 20px rgba(34, 197, 94, 0.15)',
              }}
              className="hover-lift"
            >
              <div
                style={{
                  width: '150px',
                  height: '150px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  margin: '0 auto 1.5rem',
                  border: '4px solid rgba(34, 197, 94, 0.5)',
                  boxShadow: '0 0 20px rgba(34, 197, 94, 0.3)',
                }}
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_ayurtech-hub/artifacts/nl3z43tc_Dr%20Kumar.jpg"
                  alt="Dr. Kumar"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
              <h3 className="heading-3" style={{ marginBottom: '0.5rem', color: 'var(--text-light)' }}>
                Dr. Kumar
              </h3>
              <div
                style={{
                  background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.3) 0%, rgba(34, 197, 94, 0.1) 100%)',
                  borderRadius: '2rem',
                  padding: '0.35rem 1rem',
                  display: 'inline-block',
                  marginBottom: '1rem',
                  border: '1px solid rgba(34, 197, 94, 0.5)',
                }}
              >
                <span className="caption" style={{ fontWeight: '600', color: '#22c55e' }}>
                  {t('about.founderCeo')}
                </span>
              </div>
              <p className="body-small" style={{ marginBottom: '0.75rem', fontWeight: '500', color: '#22c55e' }}>
                {t('about.ayurvedicDoctor')}
              </p>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                {t('about.founderDesc')}
              </p>
            </div>

            {/* Piyush Goel */}
            <div
              style={{
                background: 'rgba(34, 197, 94, 0.1)',
                borderRadius: '1rem',
                padding: '2rem',
                textAlign: 'center',
                border: '2px solid rgba(34, 197, 94, 0.3)',
                boxShadow: '0 4px 20px rgba(34, 197, 94, 0.15)',
              }}
              className="hover-lift"
            >
              <div
                style={{
                  width: '150px',
                  height: '150px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  margin: '0 auto 1.5rem',
                  border: '4px solid rgba(34, 197, 94, 0.5)',
                  boxShadow: '0 0 20px rgba(34, 197, 94, 0.3)',
                }}
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_ayurtech-hub/artifacts/cqc9yyme_Piyush%20Goel.jpg"
                  alt="Piyush Goel"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
              <h3 className="heading-3" style={{ marginBottom: '0.5rem', color: 'var(--text-light)' }}>
                Piyush Goel
              </h3>
              <div
                style={{
                  background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.3) 0%, rgba(34, 197, 94, 0.1) 100%)',
                  borderRadius: '2rem',
                  padding: '0.35rem 1rem',
                  display: 'inline-block',
                  marginBottom: '1rem',
                  border: '1px solid rgba(34, 197, 94, 0.5)',
                }}
              >
                <span className="caption" style={{ fontWeight: '600', color: '#22c55e' }}>
                  {t('about.cbo')}
                </span>
              </div>
              <p className="body-small" style={{ marginBottom: '0.75rem', fontWeight: '500', color: '#22c55e' }}>
                {t('about.technocrat')}
              </p>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                {t('about.cboDesc')}
              </p>
            </div>

            {/* Dr. Sushila */}
            <div
              style={{
                background: 'rgba(34, 197, 94, 0.1)',
                borderRadius: '1rem',
                padding: '2rem',
                textAlign: 'center',
                border: '2px solid rgba(34, 197, 94, 0.3)',
                boxShadow: '0 4px 20px rgba(34, 197, 94, 0.15)',
              }}
              className="hover-lift"
            >
              <div
                style={{
                  width: '150px',
                  height: '150px',
                  borderRadius: '50%',
                  overflow: 'hidden',
                  margin: '0 auto 1.5rem',
                  border: '4px solid rgba(34, 197, 94, 0.5)',
                  boxShadow: '0 0 20px rgba(34, 197, 94, 0.3)',
                }}
              >
                <img 
                  src="1000507035.jpg"
                  alt="Dr. Sushila D Sangwan"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
              <h3 className="heading-3" style={{ marginBottom: '0.5rem', color: 'var(--text-light)' }}>
                Dr. Sushila D Sangwan
              </h3>
              <div
                style={{
                  background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.3) 0%, rgba(34, 197, 94, 0.1) 100%)',
                  borderRadius: '2rem',
                  padding: '0.35rem 1rem',
                  display: 'inline-block',
                  marginBottom: '1rem',
                  border: '1px solid rgba(34, 197, 94, 0.5)',
                }}
              >
                <span className="caption" style={{ fontWeight: '600', color: '#22c55e' }}>
                  {t('about.cro')}
                </span>
              </div>
              <p className="body-small" style={{ marginBottom: '0.75rem', fontWeight: '500', color: '#22c55e' }}>
                {t('about.researchScientist')}
              </p>
              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                {t('about.croDesc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      <ContactForm />
    </div>
  );
};

export default AboutPage;
